import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_APPLICATION_RULES_GRID } from '../../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  appPathCellCustomRenderer,
  Column,
  ColumnKeys,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  PREDEFINED_FILTERS_KEYS,
} from './grid-config';
import { GzContentControlApplicationRulesGridDataProvider } from './GzContentControlApplicationRulesGridDataProvider';
import { html, TemplateResult } from 'lit';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { ContentControlApplicationRule } from '@nirvana/shared/dtos/types/policies/common';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../../ui/gzButton/types';
import { GzButtonWebComponent } from '../../../../../../../../ui/gzButton';
import { FontSizes, FontWeights } from '../../../../../../../../types/common/types';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzContentControlApplicationRulesFormWebComponent } from './rulesItemForm';
import { validateDuplicates } from './utils/validators';

@customElement(WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_APPLICATION_RULES_GRID)
export class GzContentControlApplicationRulesGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  ContentControlApplicationRule,
  ContentControlApplicationRule,
  GzContentControlApplicationRulesGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected columnRenderers = {
    [ColumnKeys.APP_PATH]: appPathCellCustomRenderer.bind(this, this.onRuleNameClick.bind(this)),
  };
  protected showHeaderCustomActionsInsideGrid = true;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

  protected hasSelectableRows = true;
  protected hasBasicEditRowAction = true;
  protected hasEditRowAction = false;

  protected translations = {
    editRow: translateFn('Edit'),
    deleteRow: translateFn('Delete'),
  };

  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;

  protected getDataProvider(): GzContentControlApplicationRulesGridDataProvider {
    return new GzContentControlApplicationRulesGridDataProvider(this.initialGridItems);
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);
    this.deleteButton.disabled = !event.detail.selectionStoreData.length;
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return html`
      <gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.LIGHT}">${translateFn('No rules')}</gz-text>

      <gz-button
        id="empty-state-add-button"
        type="${ButtonTypes.QUIET}"
        buttonHeight="${ButtonHeights.SMALL}"
        buttonFontSize="${ButtonFontSizes.SMALL}"
        style="margin-top: 5px;"
        @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Adjust your filters or start adding rules.')}
      </gz-button>
    `;
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <gz-button id="add-button" type="${ButtonTypes.OUTLINE}" @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Add')}
      </gz-button>

      <gz-button
        id="delete-button"
        type="${ButtonTypes.OUTLINE}"
        color="${ButtonColors.NEGATIVE}"
        ?disabled
        @click="${this.onDeleteSelectedItemsWithDialog.bind(
          this,
          translateFn('Delete rules?'),
          translateFn('You are about to delete the selected rules. This action cannot be undone.')
        )}"
        >${translateFn('Delete')}
      </gz-button>
    `;
  }

  private onAddButtonClick(): void {
    const addRuleCallback = (formValue: ContentControlApplicationRule): ValidationResult => {
      return this.onNewGridItemAdd(formValue);
    };

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzContentControlApplicationRulesFormWebComponent(),
      strings: {
        title: translateFn('Add application rule'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  protected onBasicEditRow(event: CustomEvent) {
    this.onRuleNameClick(event.detail.rowData.item);
  }

  private onRuleNameClick(item: ContentControlApplicationRule): void {
    const addRuleCallback = (formValue: ContentControlApplicationRule): ValidationResult =>
      this.onEditGridItemFromPopup(formValue, item);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzContentControlApplicationRulesFormWebComponent(item),
      strings: {
        title: translateFn('Edit application rule'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: ContentControlApplicationRule): ValidationResult {
    return validateDuplicates(
      gridItemFormValue,
      (this.gridComponent.dataProvider as GzContentControlApplicationRulesGridDataProvider).items
    );
  }

  protected validateGridItemBeforeEdit(
    oldItem: ContentControlApplicationRule,
    newItem: ContentControlApplicationRule
  ): ValidationResult {
    // skip if item is not changed
    if (newItem.appPath === oldItem.appPath) {
      return { valid: true };
    }

    return this.validateGridItemBeforeAdd(newItem);
  }

  protected onGridItemsChange(): void {
    super.onGridItemsChange();
    this.dispatchEvent(new CustomEvent('change', { bubbles: true, composed: true }));
  }
}
